import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  DollarSign, ShoppingCart, TrendingUp, Users, Eye, Play,
  Package, ArrowUpRight, ArrowDownRight, Mail, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_COLORS = {
  'CB-HERB-01': '#2D5016',
  'CB-CITRUS-01': '#D4540A',
  'CB-SMOKY-01': '#5C3D2E',
  'CB-SEAFOOD-01': '#1E4D6B',
  'CB-FULLSET-4': '#8B5CF6',
};
const PIE_COLORS = ['#D4540A', '#2D5016', '#1E4D6B', '#5C3D2E', '#8B5CF6'];
const SEASONING_LABELS = {
  'herb-savory': 'Herb & Savory',
  'citrus-garlic': 'Citrus Garlic',
  'smoky-savory': 'Smoky Savory',
  'seafood-seasoning': 'Seafood Seasoning',
  'full-set': 'Full Set',
  'general': 'General',
};

const KpiCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-[#1a1a1a] rounded-xl p-5 border border-[#2a2a2a]" data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
    <p className="text-[#888] text-xs uppercase tracking-wider mb-1">{label}</p>
    <p className="text-white text-2xl font-bold">{value}</p>
    {sub && <p className="text-[#555] text-xs mt-1">{sub}</p>}
  </div>
);

const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    {children}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#222] border border-[#333] rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-[#999] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.name === 'Revenue' ? `$${p.value.toFixed(2)}` : p.value}
        </p>
      ))}
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [kpis, setKpis] = useState(null);
  const [products, setProducts] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [trends, setTrends] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [videoEngagement, setVideoEngagement] = useState(null);
  const [trendDays, setTrendDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestHistory, setDigestHistory] = useState([]);
  const [weekComparison, setWeekComparison] = useState(null);

  const fetchAll = async () => {
    const headers = getAuthHeaders();
    try {
      const [kpiRes, prodRes, custRes, trendRes, trafficRes, videoRes, digestRes, compRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/kpis`, { headers }),
        axios.get(`${API}/admin/analytics/products`, { headers }),
        axios.get(`${API}/admin/analytics/customers`, { headers }),
        axios.get(`${API}/admin/analytics/trends?days=${trendDays}`, { headers }),
        axios.get(`${API}/admin/analytics/traffic`, { headers }),
        axios.get(`${API}/admin/analytics/video-engagement`, { headers }),
        axios.get(`${API}/admin/analytics/digest-history`, { headers }),
        axios.get(`${API}/admin/analytics/digest-data`, { headers }),
      ]);
      setKpis(kpiRes.data);
      setProducts(prodRes.data);
      setCustomers(custRes.data);
      setTrends(trendRes.data);
      setTraffic(trafficRes.data);
      setVideoEngagement(videoRes.data);
      setDigestHistory(digestRes.data.history || []);
      setWeekComparison(compRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [trendDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrendToggle = (days) => {
    setTrendDays(days);
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/analytics/send-digest`, {}, { headers });
      toast.success('Weekly digest sent successfully!');
      // Refresh digest history
      const res = await axios.get(`${API}/admin/analytics/digest-history`, { headers });
      setDigestHistory(res.data.history || []);
    } catch (error) {
      toast.error('Failed to send digest');
      console.error('Digest send error:', error);
    } finally {
      setSendingDigest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const fmt = (v) => (typeof v === 'number' ? (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v) : v);
  const fmtMoney = (v) => `$${(v || 0).toFixed(2)}`;
  const changeBadge = (pct) => {
    if (pct > 0) return <span className="inline-flex items-center gap-0.5 text-emerald-400 text-xs font-semibold"><ArrowUpRight className="w-3 h-3" />+{pct}%</span>;
    if (pct < 0) return <span className="inline-flex items-center gap-0.5 text-red-400 text-xs font-semibold"><ArrowDownRight className="w-3 h-3" />{pct}%</span>;
    return <span className="text-[#666] text-xs">0%</span>;
  };

  // Bundle vs single pie data
  const bvs = products?.bundle_vs_single || {};
  const pieData = [
    { name: 'Single', value: bvs.single?.revenue || 0 },
    { name: 'Bundle', value: bvs.bundle?.revenue || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="admin-analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Analytics</h1>
        <p className="text-[#888] text-sm">Business performance and customer insights</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-grid">
        <KpiCard label="Orders Today" value={kpis?.today_orders || 0} icon={ShoppingCart} color="#D4540A" sub={fmtMoney(kpis?.today_revenue)} />
        <KpiCard label="This Week" value={kpis?.week_orders || 0} icon={TrendingUp} color="#2D5016" sub={fmtMoney(kpis?.week_revenue)} />
        <KpiCard label="This Month" value={kpis?.month_orders || 0} icon={TrendingUp} color="#1E4D6B" sub={fmtMoney(kpis?.month_revenue)} />
        <KpiCard label="Total Revenue" value={fmtMoney(kpis?.total_revenue)} icon={DollarSign} color="#8B5CF6" sub={`${kpis?.total_orders || 0} orders`} />
        <KpiCard label="Avg Order Value" value={fmtMoney(kpis?.avg_order_value)} icon={Package} color="#5C3D2E" />
      </div>

      {/* Week-over-Week Comparison */}
      {weekComparison && (
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5" data-testid="week-comparison">
          <SectionHeader title="This Week vs Last Week">
            <span className="text-[#666] text-xs">{weekComparison.period}</span>
          </SectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Orders Comparison */}
            <div className="bg-[#111] rounded-lg p-4">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-3">Orders</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-white text-3xl font-bold">{weekComparison.orders.count}</p>
                  <p className="text-[#555] text-xs mt-0.5">this week</p>
                </div>
                <div className="text-right">
                  <div className="mb-1">{changeBadge(weekComparison.orders.order_change_pct)}</div>
                  <p className="text-[#555] text-xs">{weekComparison.orders.prev_count} last week</p>
                </div>
              </div>
              {/* Mini bar comparison */}
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#888] w-12">This wk</span>
                  <div className="flex-1 h-2 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4540A] rounded-full transition-all" style={{ width: `${Math.min(100, weekComparison.orders.count ? (weekComparison.orders.count / Math.max(weekComparison.orders.count, weekComparison.orders.prev_count || 1)) * 100 : 0)}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#888] w-12">Last wk</span>
                  <div className="flex-1 h-2 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-[#555] rounded-full transition-all" style={{ width: `${Math.min(100, weekComparison.orders.prev_count ? (weekComparison.orders.prev_count / Math.max(weekComparison.orders.count || 1, weekComparison.orders.prev_count)) * 100 : 0)}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Comparison */}
            <div className="bg-[#111] rounded-lg p-4">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-3">Revenue</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-white text-3xl font-bold">${weekComparison.orders.revenue.toFixed(2)}</p>
                  <p className="text-[#555] text-xs mt-0.5">this week</p>
                </div>
                <div className="text-right">
                  <div className="mb-1">{changeBadge(weekComparison.orders.revenue_change_pct)}</div>
                  <p className="text-[#555] text-xs">${weekComparison.orders.prev_revenue.toFixed(2)} last week</p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#888] w-12">This wk</span>
                  <div className="flex-1 h-2 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-[#2D5016] rounded-full transition-all" style={{ width: `${Math.min(100, weekComparison.orders.revenue ? (weekComparison.orders.revenue / Math.max(weekComparison.orders.revenue, weekComparison.orders.prev_revenue || 1)) * 100 : 0)}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#888] w-12">Last wk</span>
                  <div className="flex-1 h-2 bg-[#2a2a2a] rounded-full overflow-hidden">
                    <div className="h-full bg-[#555] rounded-full transition-all" style={{ width: `${Math.min(100, weekComparison.orders.prev_revenue ? (weekComparison.orders.prev_revenue / Math.max(weekComparison.orders.revenue || 1, weekComparison.orders.prev_revenue)) * 100 : 0)}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* AOV + Top Product */}
            <div className="bg-[#111] rounded-lg p-4">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-3">Avg Order Value</p>
              <p className="text-white text-3xl font-bold">${weekComparison.orders.aov.toFixed(2)}</p>
              <p className="text-[#555] text-xs mt-0.5">this week</p>
              {weekComparison.top_products?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#2a2a2a]">
                  <p className="text-[#888] text-xs uppercase tracking-wider mb-2">Top Product</p>
                  <p className="text-white text-sm font-medium">{weekComparison.top_products[0].name}</p>
                  <p className="text-[#888] text-xs">{weekComparison.top_products[0].units} units &middot; ${weekComparison.top_products[0].revenue.toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trend Charts */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
        <SectionHeader title="Order & Revenue Trends">
          <div className="flex gap-1 bg-[#111] rounded-lg p-1">
            {[7, 30].map(d => (
              <Button
                key={d}
                size="sm"
                variant={trendDays === d ? 'default' : 'ghost'}
                className={trendDays === d ? 'bg-[#D4540A] text-white h-7 text-xs' : 'text-[#888] h-7 text-xs hover:text-white'}
                onClick={() => handleTrendToggle(d)}
                data-testid={`trend-${d}d-btn`}
              >
                {d}D
              </Button>
            ))}
          </div>
        </SectionHeader>

        {trends?.trends?.length > 0 ? (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Orders Chart */}
            <div>
              <p className="text-xs text-[#666] mb-3 uppercase tracking-wider">Orders per Day</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trends.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#666' }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="orders" name="Orders" fill="#D4540A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Revenue Chart */}
            <div>
              <p className="text-xs text-[#666] mb-3 uppercase tracking-wider">Revenue per Day</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trends.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#666' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#2D5016" strokeWidth={2} dot={{ fill: '#2D5016', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <p className="text-[#666] text-sm text-center py-8">No order data for this period</p>
        )}
      </div>

      {/* Product Performance + Bundle vs Single */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Products */}
        <div className="lg:col-span-2 bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="Product Performance" />
          {products?.top_products?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="product-performance-table">
                <thead>
                  <tr className="border-b border-[#2a2a2a]">
                    <th className="text-left text-[#666] font-medium py-2 pr-4">Product</th>
                    <th className="text-right text-[#666] font-medium py-2 px-3">Units</th>
                    <th className="text-right text-[#666] font-medium py-2 px-3">Revenue</th>
                    <th className="text-right text-[#666] font-medium py-2 pl-3">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {products.top_products.map((p, i) => (
                    <tr key={p.sku} className="border-b border-[#2a2a2a]/50">
                      <td className="py-2.5 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PRODUCT_COLORS[p.sku] || '#666' }} />
                          <span className="text-white font-medium">{p.product_name}</span>
                          <span className="text-[#555] text-xs">{p.sku}</span>
                        </div>
                      </td>
                      <td className="text-right text-white py-2.5 px-3">{p.units_sold}</td>
                      <td className="text-right text-white py-2.5 px-3">${p.revenue.toFixed(2)}</td>
                      <td className="text-right text-[#888] py-2.5 pl-3">{p.order_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-8">No product data yet</p>
          )}
        </div>

        {/* Bundle vs Single Pie */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="Bundle vs Single" />
          {pieData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value">
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-2">
                {pieData.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }} />
                    <span className="text-[#999]">{d.name}</span>
                    <span className="text-white font-medium">${d.value.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {/* Units breakdown */}
              <div className="mt-4 w-full space-y-2 pt-4 border-t border-[#2a2a2a]">
                <div className="flex justify-between text-xs">
                  <span className="text-[#888]">Single units</span>
                  <span className="text-white">{bvs.single?.units || 0}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#888]">Bundle units</span>
                  <span className="text-white">{bvs.bundle?.units || 0}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-8">No sales data yet</p>
          )}
        </div>
      </div>

      {/* Customer Insights */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Customers */}
        <div className="lg:col-span-2 bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="Top Customers" />
          {customers?.top_customers?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-customers-table">
                <thead>
                  <tr className="border-b border-[#2a2a2a]">
                    <th className="text-left text-[#666] font-medium py-2 pr-4">Customer</th>
                    <th className="text-right text-[#666] font-medium py-2 px-3">Orders</th>
                    <th className="text-right text-[#666] font-medium py-2 pl-3">Total Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.top_customers.map((c) => (
                    <tr key={c.email} className="border-b border-[#2a2a2a]/50">
                      <td className="py-2.5 pr-4">
                        <p className="text-white font-medium">{c.customer_name}</p>
                        <p className="text-[#555] text-xs">{c.email}</p>
                      </td>
                      <td className="text-right text-white py-2.5 px-3">{c.order_count}</td>
                      <td className="text-right text-white font-medium py-2.5 pl-3">${c.total_spend.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-8">No customer data yet</p>
          )}
        </div>

        {/* New vs Returning */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="New vs Returning" />
          {customers?.new_vs_returning ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-[#2a2a2a]">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D4540A]" />
                  <span className="text-[#999] text-sm">New Customers</span>
                </div>
                <span className="text-white text-xl font-bold">{customers.new_vs_returning.new}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#2a2a2a]">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#2D5016]" />
                  <span className="text-[#999] text-sm">Returning</span>
                </div>
                <span className="text-white text-xl font-bold">{customers.new_vs_returning.returning}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-[#999] text-sm">Total Customers</span>
                <span className="text-white text-xl font-bold">{customers.new_vs_returning.total}</span>
              </div>
              {customers.new_vs_returning.total > 0 && (
                <div className="pt-2">
                  <div className="w-full h-3 bg-[#2a2a2a] rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-[#D4540A]"
                      style={{ width: `${(customers.new_vs_returning.new / customers.new_vs_returning.total) * 100}%` }}
                    />
                    <div
                      className="h-full bg-[#2D5016]"
                      style={{ width: `${(customers.new_vs_returning.returning / customers.new_vs_returning.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-[#666] mt-1">
                    <span>{Math.round((customers.new_vs_returning.new / customers.new_vs_returning.total) * 100)}% new</span>
                    <span>{Math.round((customers.new_vs_returning.returning / customers.new_vs_returning.total) * 100)}% returning</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-8">No data yet</p>
          )}
        </div>
      </div>

      {/* Traffic + Video Engagement */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Traffic */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="Page Traffic (30 days)" />
          <div className="mb-4 flex gap-4">
            <div className="text-center">
              <p className="text-white text-xl font-bold">{traffic?.total_visits_30d || 0}</p>
              <p className="text-[#666] text-xs">30-day visits</p>
            </div>
            <div className="text-center">
              <p className="text-white text-xl font-bold">{traffic?.total_visits_7d || 0}</p>
              <p className="text-[#666] text-xs">7-day visits</p>
            </div>
          </div>
          {traffic?.pages?.length > 0 ? (
            <div className="space-y-2" data-testid="traffic-list">
              {traffic.pages.map((p) => (
                <div key={p.page} className="flex items-center justify-between py-2 border-b border-[#2a2a2a]/50">
                  <div className="flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5 text-[#666]" />
                    <span className="text-white text-sm capitalize">{p.page}</span>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-[#888]">{p.visits_7d} <span className="text-[#555]">7d</span></span>
                    <span className="text-white font-medium">{p.visits_30d} <span className="text-[#555]">30d</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-4">No traffic data yet. Visits are tracked automatically.</p>
          )}
        </div>

        {/* Video Engagement */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5">
          <SectionHeader title="Video Engagement (30 days)" />
          <div className="mb-4">
            <p className="text-white text-xl font-bold">{videoEngagement?.total_clicks || 0}</p>
            <p className="text-[#666] text-xs">Total video clicks</p>
          </div>
          {videoEngagement?.videos?.length > 0 ? (
            <div className="space-y-2" data-testid="video-engagement-list">
              {videoEngagement.videos.map((v) => (
                <div key={v.video_id} className="flex items-center justify-between py-2 border-b border-[#2a2a2a]/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Play className="w-3.5 h-3.5 text-[#D4540A]" />
                      <span className="text-white text-sm truncate">{v.video_title || v.video_id}</span>
                    </div>
                    {v.related_product && (
                      <span className="text-[#666] text-xs ml-5 capitalize">
                        {SEASONING_LABELS[v.related_product] || v.related_product}
                      </span>
                    )}
                  </div>
                  <span className="text-white font-medium text-sm ml-3">{v.clicks} clicks</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#666] text-sm text-center py-4">No video clicks tracked yet.</p>
          )}
        </div>
      </div>

      {/* Weekly Digest */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-5" data-testid="digest-section">
        <SectionHeader title="Weekly Email Digest">
          <Button
            size="sm"
            onClick={handleSendDigest}
            disabled={sendingDigest}
            className="bg-[#D4540A] hover:bg-[#B84709] text-white h-8 text-xs"
            data-testid="send-digest-btn"
          >
            {sendingDigest ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
            {sendingDigest ? 'Sending...' : 'Send Now'}
          </Button>
        </SectionHeader>
        <p className="text-[#666] text-xs mb-4">
          Automatically sent every Monday at 8:00 AM UTC to management@countrybofoods.com
        </p>
        {digestHistory.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-[#888] uppercase tracking-wider mb-2">Recent Sends</p>
            {digestHistory.slice(0, 5).map((run, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#2a2a2a]/50 text-sm">
                <div>
                  <span className="text-white">{run.period}</span>
                  <span className="text-[#555] text-xs ml-2">
                    {run.summary?.orders || 0} orders, ${(run.summary?.revenue || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {run.results?.map((r, j) => (
                    <span key={j} className={`text-xs px-2 py-0.5 rounded ${r.status === 'sent' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                      {r.status}
                    </span>
                  ))}
                  <span className="text-[#555] text-xs">{new Date(run.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#666] text-sm text-center py-4">No digests sent yet. Click "Send Now" to send a test.</p>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
